import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import React from "react";
import { Button } from "components/haven-design/buttons";
import {
  DownloadInterestPDFMeta,
  DownloadInterestPDFMetaVariables,
  DownloadInterestPDFMeta_interest as Interest,
} from "types/gql-types";
import { filenameFromContentDisposition, triggerBlobDownload } from "utils/download-file";

type Props = {
  interestID: string;
}

const QUERY_DOWNLOAD_PDF = gql`
  query DownloadInterestPDFMeta($interestID: ID!) {
    interest(id: $interestID) {
      id
      signatureId
      documents {
        name
        downloadUrl
        category
      }
    }
  }
`;

const getDownloadUrl = (interest: Interest) => {
  if (interest.signatureId) {
    return `/api/dl/signed-pdf?id=${interest.id}`;
  }

  return interest.documents.find(doc => doc.category === "Pre Application")?.downloadUrl ?? null;
};

const DownloadPDF: React.FC<Props> = (props) => {
  const variables = { interestID: props.interestID };
  const query = useQuery<DownloadInterestPDFMeta, DownloadInterestPDFMetaVariables>(
    QUERY_DOWNLOAD_PDF,
    { variables },
  );
  const [isDownloading, setIsDownloading] = React.useState(false);

  const onDownloadForm = (appUrl: string) => {
    const fallbackFilename = `application-${Date.now()}.pdf`;
    const req = new XMLHttpRequest();
    req.open("GET", appUrl, true);
    req.responseType = "blob";

    req.onload = () => {
      if (req.status === 200) {
        const fileName = filenameFromContentDisposition(
          req.getResponseHeader("content-disposition"),
          fallbackFilename,
        );
        triggerBlobDownload(req.response, fileName);
        setIsDownloading(false);
        setTimeout(() => alert(`Saving ${fileName} to your downloads folder.`), 200);
      } else if (req.status === 404) {
        setIsDownloading(false);
        alert("File not found.");
      } else {
        setIsDownloading(false);
        alert("Error downloading PDF file");
      }
    };
    setIsDownloading(true);
    req.send();
  };

  if (query.error || query.loading || !query.data) return null;

  const appUrl = getDownloadUrl(query.data.interest);

  if (!appUrl) return null;

  return (
    <Button id="download-as-pdf" size="S" color="secondary" onClick={() => onDownloadForm(appUrl)} disabled={isDownloading}>
      {isDownloading ? "Downloading..." : "Download as PDF"}
    </Button>
  );
};

export default DownloadPDF;
